import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NearMeIcon from '@mui/icons-material/NearMe';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Service } from '../../types/api.types';
import type { Language } from '../../types/lang.types';
import { getServices } from '../../services/storage';
import SEOHead from '../../components/SEOHead';

const MotionBox = motion(Box);

const SERVICE_IMAGES: Record<number, string> = {
  1: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80&auto=format&fit=crop',
  2: 'https://images.unsplash.com/photo-1590650153855-d9e808231d41?w=800&q=80&auto=format&fit=crop',
  3: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80&auto=format&fit=crop',
  4: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80&auto=format&fit=crop',
  5: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800&q=80&auto=format&fit=crop',
  6: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80&auto=format&fit=crop',
  7: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80&auto=format&fit=crop',
};

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80&auto=format&fit=crop';

const ServicesPage: React.FC = () => {
  const { currentLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);

  useEffect(() => {
    getServices().then((list) => setServices(list.filter((s) => s.is_active))).catch(() => {});
  }, []);

  const titleKey = `title_${currentLanguage}` as `title_${Language}`;
  const descKey = `description_${currentLanguage}` as `description_${Language}`;

  return (
    <Box>
      <SEOHead
        title={t('seo.services_title')}
        description={t('seo.services_description')}
        canonicalPath="/services"
      />
      {/* Page Header */}
      <Box sx={{ bgcolor: '#f8f8f8', borderBottom: '1px solid #e8e8e8' }}>
        <Container maxWidth="lg" sx={{ py: 10 }}>
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', letterSpacing: '3px', mb: 2, display: 'block' }}
          >
            {t('services.title')}
          </Typography>
          <Typography variant="h1" sx={{ mb: 3, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            {t('services.subtitle')}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', maxWidth: 520, fontSize: '1.1rem', lineHeight: 1.75 }}
          >
            {t('services.description')}
          </Typography>
        </Container>
      </Box>

      {/* Services Grid */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={3}>
          {services.map((service, index) => {
            const imgSrc = SERVICE_IMAGES[service.order_index] ?? FALLBACK_IMAGE;
            return (
              <Grid item xs={12} sm={6} key={service.id}>
                <MotionBox
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  viewport={{ once: true }}
                  onClick={() => navigate(`/services/${service.id}`)}
                  sx={{
                    height: 520,
                    borderRadius: 2,
                    overflow: 'hidden',
                    position: 'relative',
                    cursor: 'pointer',
                    backgroundImage: `url(${imgSrc})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      '& .card-blur-overlay': { opacity: 1 },
                      '& .card-hover-info': { opacity: 1, transform: 'translateY(0)' },
                      '& .card-default-content': { opacity: 0 },
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(160deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.72) 100%)',
                      zIndex: 1,
                    },
                  }}
                >
                  {/* Default content */}
                  <Box className="card-default-content" sx={{ transition: 'opacity 0.3s ease', zIndex: 2, position: 'absolute', inset: 0 }}>
                    <Typography
                      variant="overline"
                      sx={{ position: 'absolute', top: 16, left: 16, color: 'white', letterSpacing: '2px', fontSize: '0.65rem', fontWeight: 600 }}
                    >
                      {t('common.service_label')}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ position: 'absolute', bottom: 52, left: 16, right: 16, color: 'white', fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.25 }}
                    >
                      {service[titleKey]}
                    </Typography>
                    <Box sx={{ position: 'absolute', bottom: 18, left: 16, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>{t('common.click_to_view')}</Typography>
                      <NearMeIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }} />
                    </Box>
                  </Box>

                  {/* Blur overlay */}
                  <Box
                    className="card-blur-overlay"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      backdropFilter: 'blur(8px)',
                      bgcolor: 'rgba(0,0,0,0.55)',
                      zIndex: 3,
                      opacity: 0,
                      transition: 'opacity 0.35s ease',
                    }}
                  />

                  {/* Hover info */}
                  <Box
                    className="card-hover-info"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'flex-start',
                      px: 3,
                      py: 4,
                      opacity: 0,
                      transform: 'translateY(12px)',
                      transition: 'opacity 0.35s ease, transform 0.35s ease',
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{ color: 'rgba(255,255,255,0.6)', letterSpacing: '2px', fontSize: '0.65rem', mb: 1.5 }}
                    >
                      {t('common.service_label')}
                    </Typography>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 2, lineHeight: 1.3 }}>
                      {service[titleKey]}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.7, mb: 3, fontSize: '0.9rem' }}
                    >
                      {service[descKey]}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'white' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.8rem', letterSpacing: '1px', textTransform: 'uppercase' }}>
                        {t('common.learn_more')}
                      </Typography>
                      <ArrowForwardIcon sx={{ fontSize: 14 }} />
                    </Box>
                  </Box>
                </MotionBox>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default ServicesPage;
